// src/components/dashboard/TaskChecklist.jsx
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Box, Typography, TextField, Avatar, Grid } from '@mui/material';
import { Description, CheckBox } from '@mui/icons-material';
import axios from 'axios';
import { API_GET_CHECKLIST_COUNT, API_GET_TASKS_COUNT } from '../../Util/URLConstants';
import {
  STATUS_PENDING,
  STATUS_INPROGRESS,
  STATUS_INCOMPLETED,
  STATUS_COMPLETED
} from '../../Util/VMConstants';
import MUITypography from '../../Components/MUITypography';
import { align_column, VM_Colors, vmPageTitleSx, vmStatValueSx } from '../../Styles/VMStyles';

/**
 * Normalize a status into canonical keys:
 *  - 'notStarted'  => Not Yet Started / Pending / In Completed
 *  - 'inProgress'  => In Progress
 *  - 'completed'   => Completed
 *
 * This prefers the exported constants but also falls back to tolerant string checks.
 */
const normalizeStatus = (raw) => {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();

  // Prefer exact matches to constants (case-sensitive typically)
  if (s === STATUS_COMPLETED) return 'completed';
  if (s === STATUS_INPROGRESS) return 'inProgress';
  if (s === STATUS_PENDING) return 'notStarted';
  if (s === STATUS_INCOMPLETED) return 'notStarted';

  // Fallback tolerant checks (case-insensitive)
  const lower = s.toLowerCase();
  if (lower === 'completed' || lower.includes('done') || lower.includes('finished')) return 'completed';
  if (lower === 'in progress' || lower === 'in_progress' || lower.includes('progress') || lower.includes('ongoing')) return 'inProgress';
  if (lower === 'pending' || lower.includes('not') || lower.includes('todo') || lower.includes('to do') || lower.includes('in completed') || lower.includes('incompleted')) return 'notStarted';

  // Last resort: unknown -> treat as notStarted to surface action
  return 'notStarted';
};

/**
 * Summarize a list of { count, task_status | checklist_status | status | label } into canonical counts
 */
const summarizeCounts = (list = []) => {
  const summary = { total: 0, notStarted: 0, inProgress: 0, completed: 0 };

  if (!Array.isArray(list)) return summary;

  for (const item of list) {
    const rawStatus =
      item.task_status ?? item.checklist_status ?? item.status ?? item.label ?? item.state ?? null;
    const normalized = normalizeStatus(rawStatus);
    const count = Number(item.count ?? 0);
    if (!Number.isFinite(count)) continue;

    summary.total += count;

    if (normalized === 'completed') summary.completed += count;
    else if (normalized === 'inProgress') summary.inProgress += count;
    else summary.notStarted += count;
  }

  return summary;
};

const TaskChecklist = ({ defaultFrom, defaultTo , userID ,subscriptionStatus}) => {
  const [fromDate, setFromDate] = useState(defaultFrom || '');
  const [toDate, setToDate] = useState(defaultTo || '');
  const [tasksSummaryRaw, setTasksSummaryRaw] = useState([]);
  const [checklistSummaryRaw, setChecklistSummaryRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Seed defaults
  useEffect(() => {
    if (defaultFrom) setFromDate(defaultFrom);
  }, [defaultFrom]);

  useEffect(() => {
    if (defaultTo) setToDate(defaultTo);
  }, [defaultTo]);

  const fetchCounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      if (userID) params.user_id = userID;

      const [tasksRes, checklistRes] = await Promise.all([
        axios.get(API_GET_TASKS_COUNT, { params }),
        axios.get(API_GET_CHECKLIST_COUNT, { params })
      ]);

      const tasksData = tasksRes?.data?.data ?? tasksRes?.data ?? [];
      const checklistData = checklistRes?.data?.data ?? checklistRes?.data ?? [];

      setTasksSummaryRaw(Array.isArray(tasksData) ? tasksData : []);
      setChecklistSummaryRaw(Array.isArray(checklistData) ? checklistData : []);
    } catch (err) {
      console.error('TaskChecklist fetch error:', err);
      setError('Failed to load task/checklist counts');
      setTasksSummaryRaw([]);
      setChecklistSummaryRaw([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const tasksData = useMemo(() => summarizeCounts(tasksSummaryRaw), [tasksSummaryRaw]);
  const checklistData = useMemo(() => summarizeCounts(checklistSummaryRaw), [checklistSummaryRaw]);

  return (
    <Card sx={{ boxShadow: 1 ,border:'1px solid #E0E0E0'}}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ ...align_column, alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          
          <MUITypography variant="pageTitle" sx={{ ...vmPageTitleSx, mb: 1 }}> Tasks & Checklists</MUITypography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="From"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 140 }}
            />
            <TextField
              label="To"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 140 }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Tasks card */}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.light' }}>
                <Description sx={{ width: 20, height: 20, color: VM_Colors.white }} />
              </Avatar>
              <Typography variant="subtitle1" sx={{fontSize:'18px', fontWeight: 600 }}>
                Total Tasks - {loading ? '...' : tasksData.total}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{ bgcolor: '#FFEBEE', borderRadius: 1.5, p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Not Yet Started
                  </Typography>
                  <Typography variant="statValue" sx={vmStatValueSx}>
                    {loading ? '...' : tasksData.notStarted}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={4}>
                <Box sx={{ bgcolor: '#E3F2FD', borderRadius: 1.5, p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    In Progress
                  </Typography>
                  <Typography variant="statValue" sx={vmStatValueSx}>
                    {loading ? '...' : tasksData.inProgress}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={4}>
                <Box sx={{ bgcolor: '#E8F5E9', borderRadius: 1.5, p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Completed
                  </Typography>
                  <Typography variant="statValue" sx={vmStatValueSx}>
                    {loading ? '...' : tasksData.completed}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Checklist card */}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.light' }}>
                <CheckBox sx={{ width: 20, height: 20, color: VM_Colors.white }} />
              </Avatar>
              <Typography variant="subtitle1" sx={{ fontSize:'18px', fontWeight: 600 }}>
                Total Checklist - {loading ? '...' : checklistData.total}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{ bgcolor: '#FFEBEE', borderRadius: 1.5, p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Not Yet Started
                  </Typography>
                  <Typography variant="statValue" sx={vmStatValueSx}>
                    {loading ? '...' : checklistData.notStarted}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={4}>
                <Box sx={{ bgcolor: '#E3F2FD', borderRadius: 1.5, p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    In Progress
                  </Typography>
                  <Typography variant="statValue" sx={vmStatValueSx}>
                    {loading ? '...' : checklistData.inProgress}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={4}>
                <Box sx={{ bgcolor: '#E8F5E9', borderRadius: 1.5, p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Completed
                  </Typography>
                  <Typography variant="statValue" sx={vmStatValueSx}>
                    {loading ? '...' : checklistData.completed}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {error && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="error">{error}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TaskChecklist;